import React, { useState } from 'react';
import Markdown from 'react-markdown';
import {
    CheckIcon,
    SparklesIcon,
    XCircleIcon,
    XMarkIcon,
    UserIcon,
    CalendarIcon,
    DocumentTextIcon,
    TicketIcon,
    GiftIcon,
    ClockIcon,
    ArrowPathIcon,
    ShareIcon,
    ArchiveBoxIcon,
    ChartBarIcon,
    ListBulletIcon
} from '@heroicons/react/24/outline';
import { WrenchIcon, Squares2X2Icon } from '@heroicons/react/24/solid';
import useBusinessPostApi from '~/lib/useBusinessPostApi.js';
import { ensureObjectArray, ucallwords, ucfirst } from '~/lib/Utils.js';
import { useBookableResourcesQuery, useEventTypesQuery } from '~/lib/Queries';
import remarkGfm from 'remark-gfm';
import { formatDateTimeLong, formatTime, parseTime } from '~/lib/DateTime.js';
import DeleteCircleIcon from '~/components/icons/DeleteCircleIcon.jsx';
import { ToolArguments } from '~/components/assistant/ToolArguments.jsx';

const verbToIng = {
    Create: 'Creating',
    Update: 'Updating',
    Ban: 'Banning',
    List: 'Listing',
    Delete: 'Deleting',
};

function humanToolName(name, isRunning) {
    name = name.split('-').pop();
    name = ucallwords(name.replace(/_/g, ' '));
    // If the tool is running grab the first word and add 'ing' on the end if the first word is Create, Update, or Ban
    if (isRunning) {
        const firstWord = name.split(' ')[0];
        if (Object.keys(verbToIng).includes(firstWord)) {
            name = name.replace(firstWord, verbToIng[firstWord]);
        }
    }
    return name;
}

function toolCallIcon(toolName) {
    const className = 'w-5 h-5 mr-2 mt-0.5 text-primary-700';

    // Map tool call names to their corresponding icons based on the screenshot
    if (toolName.includes('banned') || toolName.includes('ban')) {
        return <XCircleIcon className={className} />;
    }
    if (toolName.includes('resource') || toolName.includes('Resource')) {
        return <Squares2X2Icon className={className} />;
    }
    if (toolName.includes('client') || toolName.includes('Client')) {
        return <UserIcon className={className} />;
    }
    if (toolName.includes('event_type') || toolName.includes('Event Type')) {
        return <CalendarIcon className={className} />;
    }
    if (toolName.includes('question') || toolName.includes('Question')) {
        return <DocumentTextIcon className={className} />;
    }
    if (toolName.includes('coupon') || toolName.includes('Coupon')) {
        return <TicketIcon className={className} />;
    }
    if (toolName.includes('gift') || toolName.includes('Gift')) {
        return <GiftIcon className={className} />;
    }
    if (toolName.includes('automation') || toolName.includes('Automation')) {
        return <ArrowPathIcon className={className} />;
    }
    if (toolName.includes('integration') || toolName.includes('Integration')) {
        return <ShareIcon className={className} />;
    }
    if (toolName.includes('package') || toolName.includes('Package')) {
        return <ArchiveBoxIcon className={className} />;
    }
    if (toolName.includes('report') || toolName.includes('Report')) {
        return <ChartBarIcon className={className} />;
    }
    if (toolName.includes('scheduling') || toolName.includes('Scheduling')) {
        return <ClockIcon className={className} />;
    }
    if (toolName.includes('subscription') || toolName.includes('Subscription')) {
        return <ArrowPathIcon className={className} />;
    }
    if (toolName.includes('waitlist') || toolName.includes('Waitlist')) {
        return <ListBulletIcon className={className} />;
    }

    // Fallback to WrenchIcon if no matching icon is found
    return <WrenchIcon className={className} />;
}

function getRecommendedPrompts(message)
{
    if (typeof message !== 'string') {
            return { message: message ?? '', recommendedPrompts: [] };
    }

    // Find all text wrapped in <RECOMMENDED> and </RECOMMENDED>
    // then remove those from the original message and return the recommended prompts
    let recommendedPrompts = [];
    let newMessage = message;
    let match;
    const recommendedRegex = /<RECOMMENDED>(.*?)<\/RECOMMENDED>/gs;
    while ((match = recommendedRegex.exec(message)) !== null) {
        recommendedPrompts.push((match[1]??'').trim());
        newMessage = newMessage.replace(match[0], '');
    }

    // If there's only one option and it's too long, don't show it
    if (recommendedPrompts.length === 1 && recommendedPrompts[0].length > 50) {
        recommendedPrompts = [];
    }

    return { message: newMessage, recommendedPrompts };
}

function LinkRenderer(props) {
    return (
        <a href={props.href} target="_blank" rel="noreferrer">
            {props.children}
        </a>
    );
}

const ToolResults = ({ results, isLastMessage }) => {
    function isDeclined(result) {
        return result.result === 'User declined to run this tool';
    }

    return (
        <div className="space-y-1">
            {typeof results?.map === 'function' && results.map((result, idx) => (
                <div key={idx} className="">
                    <div className="flex">
                        {isDeclined(result) && (
                            <DeleteCircleIcon className="w-4 h-4 mr-2 mt-1 text-gray-500" />
                        )}
                        {!isDeclined(result) && (
                            toolCallIcon(result.toolName)
                        )}
                        <div>
                            {isDeclined(result) ? 'Declined: ' : ''}
                            {humanToolName(result.toolName, isLastMessage)}
                            {isLastMessage && '...'}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};


export const AssistantMessage = ({
    message,
    onToolResponse,
    onClickRecommendedPrompt,
    messages,
    isLastMessage,
    className,
    isRunning,
}) => {
    const toolCalls = ensureObjectArray(message.tool_calls);
    const [toolApprovals, setToolApprovals] = useState(
        toolCalls?.reduce((acc, tool) => {
            acc[tool.id] = true; // Default to approved
            return acc;
        }, {}) || {},
    );

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeclining, setIsDeclining] = useState(false);
    const { save: submitToolResponse } = useBusinessPostApi(
        `/assistant/tool-response`,
    );

    const hasToolCalls = !!toolCalls && toolCalls.length > 0;
    const showToolCalls = isLastMessage && hasToolCalls;

    // Tool calls that arrived after this component mounted are not in toolApprovals,
    // so default them to approved. Use this everywhere instead of reading the map
    // directly, otherwise an unknown id reads as declined and silently cancels the call.
    const isApproved = toolId => toolApprovals[toolId] ?? true;

    const handleToolToggle = toolId => {
        setToolApprovals(prev => ({
            ...prev,
            [toolId]: !(prev[toolId] ?? true),
        }));
    };

    const submit = async toolCallsWithApproval => {
        try {
            const response = await submitToolResponse({
                messageId: message.id,
                toolCalls: toolCallsWithApproval,
                messages: messages, // Include all previous messages
            });

            // Notify parent component to update messages
            if (onToolResponse && response) {
                onToolResponse(response);
            }
        } catch (error) {
            console.error('Error submitting tool selections:', error);
        }
    };

    const submitDeclineAllToolSelections = async () => {
        const toolCallsWithApproval = toolCalls.map(tool => ({
            ...tool,
            approved: false,
        }));
        try {
            setIsDeclining(true);
            await submit(toolCallsWithApproval);
        } finally {
            setIsDeclining(false);
        }
    };

    const submitToolSelections = async () => {
        if (!hasToolCalls) return;

        // Include all tool calls, marking which ones are approved
        const toolCallsWithApproval = toolCalls.map(tool => ({
            ...tool,
            approved: isApproved(tool.id),
        }));

        try {
            setIsSubmitting(true);
            await submit(toolCallsWithApproval);
        } finally {
            setIsSubmitting(false);
        }
    };

    const role = message.role;

    if (
        role === 'assistant' &&
        !message.content &&
        (!showToolCalls || !toolCalls)
    ) {
        return;
    }

    const parsedMessage = getRecommendedPrompts(message.content);
    const showRecommendedPrompts = !showToolCalls && parsedMessage.recommendedPrompts.length > 0 && isLastMessage && !isRunning;

    return (
        <div>
            <div
                className={`flex role-${role} ${role === 'user' ? 'justify-end sm:pl-16' : 'sm:pr-16'} ${!isLastMessage ? 'mb-4' : ''}`}
            >
                <div
                    className={`${className}  p-4 rounded-[22px] ${showToolCalls ? 'w-full' : ''} ${role === 'user' ? 'bg-gradient-to-r from-primary-700 to-[#96C6FF] text-white' : 'assistant-ai-message'} ${role === 'tool' ? 'bg-gray-100 border' : ''}`}
                >
                    {typeof message.content === 'string' && (
                        <Markdown
                            remarkPlugins={[remarkGfm]}
                            components={{ a: LinkRenderer }}
                        >
                            {parsedMessage.message}
                        </Markdown>
                    )}
                    {typeof message.content !== 'string' &&
                        message.role === 'tool' && (
                            <ToolResults results={message.content} isLastMessage={isLastMessage} />
                        )}
                    {showToolCalls && (
                        <div className="">
                            <h4 className="font-medium mb-2">Suggested Actions</h4>

                            {toolCalls.map(tool => (
                                <div
                                    key={tool.id}
                                    className="mb-3 pb-3 border-b last:border-b-0"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="font-medium">
                                                {humanToolName(tool.name, false)}
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                {tool.human_description ||
                                                    'Run this action?'}
                                            </div>
                                            <details className="mt-2">
                                                <summary className="text-sm text-gray-500 cursor-pointer select-none">
                                                    Show details
                                                </summary>
                                                <div className="mt-1 bg-white rounded p-3 text-sm">
                                                    <ToolArguments
                                                        args={tool.arguments}
                                                        tool={tool.name}
                                                    />
                                                </div>
                                            </details>
                                        </div>

                                        {/*<div className="ml-4 flex items-center">*/}
                                        {/*    <button*/}
                                        {/*        type="button"*/}
                                        {/*        onClick={() =>*/}
                                        {/*            handleToolToggle(tool.id)*/}
                                        {/*        }*/}
                                        {/*        className={`p-2 rounded-full ${*/}
                                        {/*            isApproved(tool.id)*/}
                                        {/*                ? 'bg-green-100 text-green-600'*/}
                                        {/*                : 'bg-gray-100 text-gray-400'*/}
                                        {/*        }`}*/}
                                        {/*        title={*/}
                                        {/*            isApproved(tool.id)*/}
                                        {/*                ? 'Approved'*/}
                                        {/*                : 'Declined'*/}
                                        {/*        }*/}
                                        {/*    >*/}
                                        {/*        <CheckIcon className="w-5 h-5" />*/}
                                        {/*    </button>*/}

                                        {/*    <button*/}
                                        {/*        type="button"*/}
                                        {/*        onClick={() =>*/}
                                        {/*            handleToolToggle(tool.id)*/}
                                        {/*        }*/}
                                        {/*        className={`p-2 rounded-full ml-2 ${*/}
                                        {/*            !isApproved(tool.id)*/}
                                        {/*                ? 'bg-red-100 text-red-600'*/}
                                        {/*                : 'bg-gray-100 text-gray-400'*/}
                                        {/*        }`}*/}
                                        {/*        title={*/}
                                        {/*            !isApproved(tool.id)*/}
                                        {/*                ? 'Declined'*/}
                                        {/*                : 'Approved'*/}
                                        {/*        }*/}
                                        {/*    >*/}
                                        {/*        <XMarkIcon className="w-5 h-5" />*/}
                                        {/*    </button>*/}
                                        {/*</div>*/}
                                    </div>
                                </div>
                            ))}

                            {!isRunning && (
                                <div className="mt-4 flex justify-end">
                                    <button
                                        type="button"
                                        onClick={submitToolSelections}
                                        disabled={isSubmitting || isDeclining}
                                        className="btn-pill"
                                    >
                                        {isSubmitting
                                            ? 'Running...'
                                            : 'Make Changes'}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={submitDeclineAllToolSelections}
                                        disabled={isSubmitting || isDeclining}
                                        className="btn-pink-pill ml-4"
                                    >
                                        {isDeclining
                                            ? 'Declining...'
                                            : 'Cancel'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        {showRecommendedPrompts && (
            <div className="mt-2">
                <div className="flex items-center flex-wrap gap-2">
                    {parsedMessage.recommendedPrompts.map((prompt, idx) => (
                        <div
                            key={idx}
                            className="cursor-pointer w-fit max-w-full break-words px-3 py-1.5 text-gray-600 border border-gray-300  hover:border-gray-400 rounded-full flex items-center"
                            onClick={() => onClickRecommendedPrompt(prompt)}
                        >
                            {prompt}
                        </div>
                    ))}
                </div>
            </div>
        )}
        </div>
    );
};

export default AssistantMessage;
